import React from 'react'
import { useNavigate } from 'react-router-dom'

import AddDarazStore from 'app/common/Stores/Daraz/AddDarazStore'
import AddShopifyStore from 'app/common/Stores/Shopify/AddShopifyStore'
import AddWooCommerceStore from 'app/common/Stores/Woo/AddWooCommerceStore'
import {
  PixelButton,
  PixelFlexBox,
  PixelFactoryContext,
  colors,
  DcText,
} from 'pixel-base'
import queryString from 'query-string'
import { useLocalStorage } from 'shared/hooks/useLocalStorage'
import { verifyConnection } from 'utils/api-routes.util'

import StoreDaraz from 'utils/icons/store-daraz'
import StoreShopify from 'utils/icons/store-shopify'
import StoreWoocom from 'utils/icons/store-woocom'
import ManualFlow from './ManualFlow'
import {
  InFlowActions,
  InFlowContainer,
  InFlowDescription,
  InFlowTitle,
  Store,
  StoreLabel,
  StoreSidebar,
  PageHeader,
  PageTitle,
  PageSubtitle,
  FormCard,
} from './stores.styled'
import { IPixelFactory } from '../../../../../../../declarations/pixel-factory'

const stores = [
  {
    name: 'Shopify',
    slug: 'shopify',
    description: '',
    icon: <StoreShopify />,
    is_active: false,
    id: 2,
    activeFlow: 'list',
  },
  {
    name: 'Daraz',
    slug: 'daraz',
    description: '',
    icon: <StoreDaraz />,
    is_active: false,
    id: 3,
    activeFlow: 'list',
  },
  {
    name: 'WooCommerce',
    slug: 'woocommerce',
    description: '',
    icon: <StoreWoocom />,
    is_active: true,
    id: 1,
    activeFlow: 'list',
  },
]

interface VerifyConnectionResponse {
  response: {
    verified: boolean
  }
}

interface ConnectionStatus {
  available: boolean
  status: string
  verifying: boolean
  success?: string
  error?: string
}

const Stores = () => {
  const navigate = useNavigate()
  const { showSuccess } = React.useContext<IPixelFactory>(PixelFactoryContext)
  const [lastShopifyStore] = useLocalStorage<{
    auth_url: string
    favicon: string
    id: number
    title: string
    url: string
    validated: boolean
  }>('lastShopifyStore', {
    auth_url: '',
    favicon: '',
    id: 0,
    title: '',
    url: '',
    validated: false,
  })
  const [lastWooCStore] = useLocalStorage<{
    auth_url: string
    favicon: string
    id: number
    title: string
    url: string
    validated: boolean
  }>('lastWooCStore', {
    auth_url: '',
    favicon: '',
    id: 0,
    title: '',
    url: '',
    validated: false,
  })

  const [activeStore, setActiveStore] = React.useState(stores[0])
  const [activeView, setActiveView] = React.useState<
    string | 'list' | 'in-flow' | 'manual'
  >(activeStore.activeFlow)
  const [connectionStatus, setConnectionStatus] =
    React.useState<ConnectionStatus>({
      available: false,
      status: '',
      verifying: true,
    })

  const p = queryString.parse(window.location.search)
  const [validatedData, setValidatedData] = React.useState({
    auth_url: '',
    favicon: '',
    id: 0,
    title: '',
    url: '',
    validated: false,
  })

  React.useEffect(() => {
    const parsedQueryData = p
    if (parsedQueryData.success) {
      setActiveView('in-flow')
      setConnectionStatus({
        ...parsedQueryData,
        available: true,
        verifying: true,
        status: '',
      })
      if (parsedQueryData.success == '1') {
        verifyConnection(parsedQueryData).subscribe(
          (res: VerifyConnectionResponse) => {
            if (res.response.verified) {
              setConnectionStatus({
                ...parsedQueryData,
                available: true,
                verifying: false,
                status: '',
              })
              showSuccess('Successfully Added')
              navigate('/admin/stores')
            }
          },
        )
      }
      if (parsedQueryData.type) {
        if (
          parsedQueryData?.type === '2' &&
          Object.keys(lastShopifyStore).length > 0
        ) {
          setValidatedData(lastShopifyStore)
        }

        if (
          parsedQueryData?.type === '1' &&
          Object.keys(lastWooCStore).length > 0
        ) {
          setValidatedData(lastWooCStore)
        }
        setActiveStore(stores[Number(parsedQueryData?.type) - 1])
      }
    }
  }, [])

  const handleConnectClick = () => {
    window.open(validatedData.auth_url, '_blank')
  }

  const resetFlow = () => {
    setActiveView('list')
    setConnectionStatus({ available: false, status: '', verifying: true })
    setValidatedData({
      auth_url: '',
      favicon: '',
      id: 0,
      title: '',
      url: '',
      validated: false,
    })

    setActiveStore(stores[0])
    navigate('/admin/stores/new')
  }

  return (
    <PixelFlexBox
      flexDirection="column"
      alignItems="stretch"
      position="relative"
      margin="0"
      justifyContent="center"
      style={{ flexFlow: 'inherit', minHeight: '400px' }}
    >
      <PixelFlexBox
        alignItems={activeView == 'in-flow' ? 'center' : 'flex-start'}
        justifyContent={activeView == 'in-flow' ? 'center' : 'flex-start'}
        height="auto"
        flexDirection="row"
        gap="24px"
        width="75%"
      >
        {activeView != 'in-flow' && (
          <PixelFlexBox
            width="30%"
            flexDirection="column"
            justifyContent="flex-start"
            alignItems="stretch"
            style={{ flexShrink: 0 }}
          >
            <PageHeader style={{ marginBottom: '16px' }}>
              <PageTitle>Add a store</PageTitle>
              <PageSubtitle>
                Choose your platform, then connect your store.
              </PageSubtitle>
            </PageHeader>
            <StoreSidebar>
              {stores.map((store) => (
                <Store
                  key={store.slug}
                  className={store.id === activeStore.id ? 'active' : ''}
                  onClick={() => {
                    resetFlow()
                    setActiveStore(store)
                    setActiveView(store.activeFlow)
                  }}
                >
                  {store.icon}
                  <StoreLabel>{store.name}</StoreLabel>
                </Store>
              ))}
            </StoreSidebar>
          </PixelFlexBox>
        )}

        <PixelFlexBox
          flexDirection="column"
          justifyContent="center"
          alignContent="center"
          alignItems={activeView === 'in-flow' ? 'center' : 'flex-start'}
          style={{ flex: 1, minWidth: 0 }}
        >
          {activeView === 'list' ? (
            <FormCard>
              {activeStore.id === 1 && (
                <AddWooCommerceStore origin="settings" />
              )}
              {activeStore.id === 2 && <AddShopifyStore origin="settings" />}
              {activeStore.id === 3 && <AddDarazStore origin="settings" />}
            </FormCard>
          ) : activeView === 'in-flow' ? (
            <>
              <InFlowContainer>
                {connectionStatus?.success === '0' ? (
                  <>
                    <InFlowTitle>{'You are still not connected'}</InFlowTitle>
                    {connectionStatus?.error && (
                      <DcText
                        customColor={colors.danger}
                        style={{
                          margin: '10px auto',
                          maxWidth: '400px',
                          textAlign: 'center',
                        }}
                      >
                        {connectionStatus.error}
                      </DcText>
                    )}
                    <InFlowDescription>
                      {'You have not finished connecting'}
                      <strong>{validatedData.url}</strong> to PixelOne. <br />
                      <br />
                      {`Click the button below to continue or cancel if you'd
                        like to add another store.`}
                    </InFlowDescription>
                    <InFlowActions>
                      <PixelButton onClick={handleConnectClick}>
                        Connect
                      </PixelButton>
                      <PixelButton variant="secondary" onClick={resetFlow}>
                        Cancel
                      </PixelButton>
                    </InFlowActions>
                  </>
                ) : (
                  <>
                    <InFlowTitle>{'You are connected'}</InFlowTitle>
                    {connectionStatus?.verifying ? (
                      <>
                        <InFlowDescription>
                          We just need to test the connection to your store.
                          Please wait a few moments...
                        </InFlowDescription>
                      </>
                    ) : (
                      <>
                        <InFlowDescription>
                          {'We have confirmed that the connection with'}
                          <strong>{validatedData.url}</strong> is functioning
                          correctly. Just a moment...
                        </InFlowDescription>
                      </>
                    )}
                  </>
                )}
              </InFlowContainer>
            </>
          ) : (
            <ManualFlow activeStore={activeStore} onBack={resetFlow} />
          )}
        </PixelFlexBox>
      </PixelFlexBox>
    </PixelFlexBox>
  )
}

export default Stores
